import React, { useMemo, useState } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from 'recharts';
import { Check } from 'lucide-react';
import { parseBackendDate } from '../utils/datetime';

const TIMES_PER_DAY = {
    once_daily: 1,
    twice_daily: 2,
    thrice_daily: 3,
};

const BAR_COLOR = '#0d9488';
const DOSE_COLOR = '#0d9488';

const formatDayKey = (date) =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

const getMedicationList = (medications) => {
    if (Array.isArray(medications)) return medications;
    return [
        ...(medications?.current || []),
        ...(medications?.history || []),
    ];
};

const findMedication = (medications, medId) =>
    getMedicationList(medications).find((m) => m.id === medId);

const resolveMedName = (entry, medications) => {
    const medId = entry.medicationId ?? entry.medication_id;
    const med = findMedication(medications, medId);
    if (!med) return medId != null ? `Medication #${medId}` : 'Unknown medication';
    return med.drugName || med.brandName || `Medication #${medId}`;
};

const slotsForMedication = (med) => {
    if (!med) return 1;
    if (med.frequency_code && TIMES_PER_DAY[med.frequency_code]) {
        return TIMES_PER_DAY[med.frequency_code];
    }
    const text = String(med.frequency || '').toLowerCase();
    if (text.includes('thrice') || text.includes('three')) return 3;
    if (text.includes('twice') || text.includes('two')) return 2;
    return 1;
};

const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;

    return (
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg">
            <p className="text-[12px] font-semibold text-gray-800">{row.day}</p>
            <p className="mt-0.5 text-[12px] text-gray-500">
                <span className="font-semibold text-teal-700">
                    {row.taken}/{row.expected}
                </span>
                {' '}
                dose{row.expected === 1 ? '' : 's'} taken
            </p>
        </div>
    );
};

const DoseMark = ({ record, dayLabel, slot }) => {
    const taken = Boolean(record);

    return (
        <span className="group/dose relative inline-flex">
            <span
                className={`flex h-4 w-4 items-center justify-center rounded ${
                    taken ? 'text-white' : 'text-gray-300'
                }`}
                style={taken ? { backgroundColor: DOSE_COLOR } : undefined}
            >
                {taken ? (
                    <Check className="h-3 w-3" strokeWidth={3} />
                ) : (
                    <span className="text-[11px] font-medium leading-none">—</span>
                )}
            </span>

            <span className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1.5 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[10px] font-medium text-white shadow-lg group-hover/dose:block">
                <span className="block text-gray-300">Dose {slot}</span>
                {taken ? (
                    <>
                        <span className="block">{dayLabel}</span>
                        <span className="block tabular-nums text-teal-300">
                            {formatTime(record.takenAt)}
                        </span>
                    </>
                ) : (
                    <>
                        <span className="block">{dayLabel}</span>
                        <span className="block text-gray-400">Not taken</span>
                    </>
                )}
            </span>
        </span>
    );
};

const MedicationTrackerChart = ({
    data = [],
    medications = [],
    selectedRange = 7,
    onRangeChange,
}) => {
    const [selectedMedId, setSelectedMedId] = useState('all');

    const medList = useMemo(() => getMedicationList(medications), [medications]);

    const medicationOptions = useMemo(() => {
        const byId = new Map();
        medList.forEach((med) => {
            if (med?.id != null && !byId.has(med.id)) {
                byId.set(med.id, med.drugName || med.brandName || `Medication #${med.id}`);
            }
        });
        (data || []).forEach((entry) => {
            if (entry?.medicationId != null && !byId.has(entry.medicationId)) {
                byId.set(entry.medicationId, resolveMedName(entry, medications));
            }
        });
        return Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
    }, [data, medList, medications]);

    const trackedMeds = useMemo(() => {
        const current = Array.isArray(medications?.current)
            ? medications.current
            : medList.filter((m) => m.is_active !== false);
        return current
            .filter((med) => selectedMedId === 'all' || String(med.id) === String(selectedMedId))
            .map((med) => ({
                id: med.id,
                name: med.drugName || med.brandName || `Medication #${med.id}`,
                slots: slotsForMedication(med),
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [medications, medList, selectedMedId]);

    const expectedPerDay = useMemo(
        () => trackedMeds.reduce((sum, med) => sum + med.slots, 0),
        [trackedMeds]
    );

    const dayColumns = useMemo(() => {
        const days = Math.max(1, Number(selectedRange) || 7);
        const cols = [];
        for (let i = 0; i < days; i += 1) {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() - i);
            cols.push({
                key: formatDayKey(d),
                label: formatDayKey(d),
                sortDate: d.getTime(),
            });
        }
        return cols;
    }, [selectedRange]);

    const intakes = useMemo(() => {
        const cutoff = new Date();
        cutoff.setHours(0, 0, 0, 0);
        cutoff.setDate(cutoff.getDate() - (Number(selectedRange) || 7) + 1);

        const end = new Date();
        end.setHours(23, 59, 59, 999);

        return (Array.isArray(data) ? data : [])
            .map((entry) => {
                const takenAt = parseBackendDate(entry.takenAt) || new Date(entry.takenAt);
                const slot = Number(entry.dose_slot ?? entry.doseSlot);
                return {
                    id: entry.id,
                    medicationId: entry.medicationId,
                    medName: resolveMedName(entry, medications),
                    takenAt,
                    doseSlot: slot === 2 || slot === 3 ? slot : 1,
                };
            })
            .filter((entry) => {
                if (!entry.takenAt || Number.isNaN(entry.takenAt.getTime())) return false;
                if (entry.takenAt < cutoff || entry.takenAt > end) return false;
                if (selectedMedId !== 'all' && String(entry.medicationId) !== String(selectedMedId)) {
                    return false;
                }
                return true;
            });
    }, [data, medications, selectedRange, selectedMedId]);

    const chartData = useMemo(() => {
        const byDay = new Map(
            [...dayColumns].reverse().map((col) => [
                col.key,
                { day: col.key, taken: 0, expected: expectedPerDay },
            ])
        );

        intakes.forEach((entry) => {
            const key = formatDayKey(entry.takenAt);
            if (!byDay.has(key)) {
                byDay.set(key, { day: key, taken: 0, expected: expectedPerDay });
            }
            byDay.get(key).taken += 1;
        });

        return Array.from(byDay.values());
    }, [intakes, dayColumns, expectedPerDay]);

    // medicationId -> dayKey -> slot -> intake
    const intakeGrid = useMemo(() => {
        const grid = new Map();
        intakes.forEach((entry) => {
            const dayKey = formatDayKey(entry.takenAt);
            if (!grid.has(entry.medicationId)) grid.set(entry.medicationId, new Map());
            const byDay = grid.get(entry.medicationId);
            if (!byDay.has(dayKey)) byDay.set(dayKey, {});
            const slots = byDay.get(dayKey);
            if (!slots[entry.doseSlot] || entry.takenAt < slots[entry.doseSlot].takenAt) {
                slots[entry.doseSlot] = entry;
            }
        });
        return grid;
    }, [intakes]);

    // Include meds that have intake history even if not currently in active list
    const tableRows = useMemo(() => {
        const rows = [...trackedMeds];
        const seen = new Set(rows.map((r) => r.id));

        intakes.forEach((entry) => {
            if (seen.has(entry.medicationId)) return;
            seen.add(entry.medicationId);
            rows.push({
                id: entry.medicationId,
                name: entry.medName,
                slots: slotsForMedication(findMedication(medications, entry.medicationId)),
            });
        });

        return rows.sort((a, b) => a.name.localeCompare(b.name));
    }, [trackedMeds, intakes, medications]);

    const maxSlots = useMemo(
        () => Math.max(1, ...tableRows.map((row) => row.slots), 1),
        [tableRows]
    );

    const totalExpected = expectedPerDay * Math.max(1, Number(selectedRange) || 7);
    const hasData = intakes.length > 0 || tableRows.length > 0;

    return (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="text-base font-semibold text-gray-800">Medication Tracker</h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                        {totalExpected > 0 ? (
                            <>
                                <span className="font-semibold text-teal-700">
                                    {intakes.length}/{totalExpected}
                                </span>
                                {' '}
                                doses taken in this period
                            </>
                        ) : (
                            `${intakes.length} dose${intakes.length === 1 ? '' : 's'} taken in this period`
                        )}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                        value={selectedMedId}
                        onChange={(e) => setSelectedMedId(e.target.value)}
                    >
                        <option value="all">All medications</option>
                        {medicationOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                                {opt.name}
                            </option>
                        ))}
                    </select>
                    <select
                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                        value={selectedRange}
                        onChange={(e) => onRangeChange?.(Number(e.target.value))}
                    >
                        <option value={1}>Today</option>
                        <option value={7}>Last 7 Days</option>
                        <option value={14}>Last 14 Days</option>
                        <option value={30}>Last 30 Days</option>
                    </select>
                </div>
            </div>

            <div className="p-5">
                {hasData ? (
                    <>
                        <div className="h-52 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                                    barCategoryGap="30%"
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                    <XAxis
                                        dataKey="day"
                                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                                        axisLine={false}
                                        tickLine={false}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                                        axisLine={false}
                                        tickLine={false}
                                        width={32}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(13, 148, 136, 0.06)' }}
                                        content={<ChartTooltip />}
                                    />
                                    <Bar
                                        dataKey="taken"
                                        fill={BAR_COLOR}
                                        radius={[6, 6, 0, 0]}
                                        maxBarSize={40}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
                            <table className="min-w-full border-collapse text-left text-[12px]">
                                <thead>
                                    <tr className="border-b border-gray-200 bg-gray-50">
                                        <th
                                            rowSpan={2}
                                            className="sticky left-0 z-10 border-r border-gray-200 bg-gray-50 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                                        >
                                            Medication
                                        </th>
                                        {dayColumns.map((col) => (
                                            <th
                                                key={col.key}
                                                colSpan={maxSlots}
                                                className="border-l border-gray-200 px-1 py-1.5 text-center text-[11px] font-semibold text-gray-600"
                                            >
                                                {col.label}
                                            </th>
                                        ))}
                                    </tr>
                                    <tr className="border-b border-gray-200 bg-gray-50/80">
                                        {dayColumns.map((col) =>
                                            Array.from({ length: maxSlots }, (_, i) => (
                                                <th
                                                    key={`${col.key}-d${i + 1}`}
                                                    className={`px-1 py-1 text-center text-[10px] font-medium text-gray-400 ${
                                                        i === 0 ? 'border-l border-gray-200' : ''
                                                    }`}
                                                >
                                                    d{i + 1}
                                                </th>
                                            ))
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableRows.map((med) => {
                                        const byDay = intakeGrid.get(med.id) || new Map();
                                        return (
                                            <tr
                                                key={med.id}
                                                className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/70"
                                            >
                                                <td className="sticky left-0 z-10 border-r border-gray-200 bg-white px-3 py-2 font-medium text-gray-800">
                                                    {med.name}
                                                </td>
                                                {dayColumns.map((col) => {
                                                    const dayDoses = byDay.get(col.key) || {};
                                                    return Array.from({ length: maxSlots }, (_, i) => {
                                                        const slot = i + 1;
                                                        const applies = slot <= med.slots;
                                                        return (
                                                            <td
                                                                key={`${col.key}-${slot}`}
                                                                className={`px-1 py-1.5 text-center ${
                                                                    i === 0 ? 'border-l border-gray-100' : ''
                                                                }`}
                                                            >
                                                                {applies ? (
                                                                    <DoseMark
                                                                        record={dayDoses[slot]}
                                                                        dayLabel={col.label}
                                                                        slot={slot}
                                                                    />
                                                                ) : (
                                                                    <span className="text-[10px] text-gray-200">·</span>
                                                                )}
                                                            </td>
                                                        );
                                                    });
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/60 py-12 text-center">
                        <p className="text-sm text-gray-500">
                            No medication intake recorded for the selected period.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MedicationTrackerChart;
